import { z } from 'zod';

import { QuestionnaireBaseSchema } from './questionnaire-base-schema';
import { getSingleValueWithOptionField } from './utils';

const MIN_EXAM_ATTEMPT_AMOUNT = 1;
const MAX_EXAM_ATTEMPT_AMOUNT = 999;

const requiredString = (message: string) =>
  z.string({ required_error: message }).trim().nonempty({ message });

// ponytail: examAttempted / examPreparations are duplicated from the Long Form
// schema; extract to a shared questionnaire-fields module if a 3rd template needs them.
const ExamAttemptedSchema = z
  .object({
    value: requiredString('questionnaire.previouslyAttemptedExam.requiredError'),
    amount: z.string().trim(),
  })
  .superRefine(({ value, amount }, ctx) => {
    if (value !== 'more-than-zero') return;

    if (amount === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['amount'],
        message:
          'questionnaire.previouslyAttemptedExam.option.moreThanZero.otherInputRequiredError',
      });
      return;
    }

    const parsed = parseInt(amount);
    if (parsed < MIN_EXAM_ATTEMPT_AMOUNT || parsed > MAX_EXAM_ATTEMPT_AMOUNT) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['amount'],
        message: 'questionnaire.previouslyAttemptedExam.option.moreThanZero.minMaxError',
      });
    }
  })
  .transform(({ value, amount }) => ({
    value,
    amount: value === 'more-than-zero' ? amount : null,
  }));

const ExamPreparationsSchema = z
  .object({
    value: z
      .array(z.string().trim())
      .min(1, { message: 'questionnaire.examPreparations.option.required' }),
    other: z.string().trim(),
  })
  .superRefine(({ value, other }, ctx) => {
    if (value.includes('other') && other.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['other'],
        message: 'questionnaire.examPreparations.otherInputRequiredError',
      });
    }
  })
  .transform(({ value, other }) => ({
    value,
    other: value.includes('other') ? other : null,
  }));

/**
 * Standard (Template A) schema — the shared base plus the Standard-only questions.
 * Reused from the base: age, highestEducationLevel, currentOrganization,
 * examAttendingObjectives, examinationSatisfaction, comment.
 */
export const QuestionnaireStandardFormSchema = QuestionnaireBaseSchema.extend({
  major: getSingleValueWithOptionField(
    'questionnaire.major.otherInputRequiredError',
    'questionnaire.major.requiredError',
  ),
  workExperience: requiredString('questionnaire.workExperience.requiredError'),
  examAttempted: ExamAttemptedSchema,
  examPreparations: ExamPreparationsSchema,
});

export type QuestionnaireStandardFormInput = z.input<typeof QuestionnaireStandardFormSchema>;
export type QuestionnaireStandardFormOutput = z.output<typeof QuestionnaireStandardFormSchema>;
